'use client'

import { useEffect, useState } from 'react'
import PropTypes from 'prop-types'
import { ArrowRightIcon } from '@repo/ui/ArrowRightIcon'
import { LargeNotesIcon } from '@repo/ui/LargeNotesIcon'
import { LoadingState } from '@repo/ui/LoadingState'
import { useThirdPartyFormWizardStore } from '@repo/store/useThirdPartyFormWizardStore'
import { WizardFooter } from '@repo/ui/WizardFooter'
import { WizardIconTitle } from '@repo/ui/WizardIconTitle'
import { WizardPanel } from '@repo/ui/WizardPanel'
import MUIAlert from '@mui/material/Alert'
import MUIBox from '@mui/material/Box'
import MUICheckbox from '@mui/material/Checkbox'
import MUIFormControlLabel from '@mui/material/FormControlLabel'
import MUIStack from '@mui/material/Stack'
import MUISwitch from '@mui/material/Switch'
import MUITypography from '@mui/material/Typography'

const formatFieldValue = (value) => {
  if (value === null || value === undefined || value === '') return null
  if (typeof value === 'object') return JSON.stringify(value)
  return String(value)
}

const ReviewDataRow = ({ row, onToggleSend }) => {
  // Highlight/arrow tracks the row's current send state directly (not isMapped alone) --
  // a read-only row is always toggled off, so it always looks the same as an unmapped
  // row, matching the ticket's own "any row that's toggled off looks like unmapped" rule.
  const isHighlighted = row.send
  const toggleDisabled = row.isReadOnly || !row.isMapped

  return (
    <MUIStack
      data-testid={`ReviewDataRow-${row.formFieldId}`}
      direction="row"
      alignItems="center"
      gap={3}
      sx={{
        width: '100%',
        padding: '12px 16px',
        borderRadius: 1,
        border: '1px solid',
        borderColor: isHighlighted ? 'secondary.main' : 'lines.grey1',
        backgroundColor: isHighlighted ? 'secondary.focus' : 'greyShades.grey1'
      }}
    >
      <MUIStack sx={{ flex: 1, minWidth: 0 }}>
        <MUITypography variant="body1" sx={{ mb: 0, fontWeight: 600 }}>
          {row.isMapped ? row.sourceFieldLabel || row.mappingType || 'Osprey field' : 'Not mapped'}
        </MUITypography>
        <MUITypography variant="body2" color="text.secondary" sx={{ mb: 0 }}>
          {row.isMapped ? (formatFieldValue(row.resolvedValue) ?? 'No value') : '—'}
        </MUITypography>
      </MUIStack>

      <MUIBox sx={{ width: '15px', flexShrink: 0, display: 'flex', justifyContent: 'center' }}>
        {isHighlighted ? <ArrowRightIcon sx={{ color: 'secondary.main' }} /> : null}
      </MUIBox>

      <MUIStack sx={{ flex: 1, minWidth: 0 }}>
        <MUITypography variant="body1" sx={{ mb: 0, fontWeight: 600 }}>
          {row.formFieldName}
        </MUITypography>
        {row.formFieldDescription ? (
          <MUITypography variant="body2" color="text.secondary" sx={{ mb: 0 }}>
            {row.formFieldDescription}
          </MUITypography>
        ) : null}
      </MUIStack>

      <MUISwitch
        checked={row.send}
        disabled={toggleDisabled}
        onChange={() => onToggleSend(row.formFieldId)}
        inputProps={{ 'aria-label': `Send ${row.formFieldName} to FormEvo` }}
      />
    </MUIStack>
  )
}

ReviewDataRow.propTypes = {
  row: PropTypes.shape({
    formFieldId: PropTypes.number.isRequired,
    formFieldName: PropTypes.string,
    formFieldDescription: PropTypes.string,
    isReadOnly: PropTypes.bool,
    isMapped: PropTypes.bool,
    mappedFormFieldId: PropTypes.number,
    resolvedValue: PropTypes.any,
    mappingType: PropTypes.string,
    sourceFieldLabel: PropTypes.string,
    send: PropTypes.bool
  }).isRequired,
  onToggleSend: PropTypes.func.isRequired
}

export const ThirdPartyFormReviewData = ({ updateStepState, wideMode, handlePrevNext, testMode = false }) => {
  const { selectedFormId, selectedMatter, fetchReviewDataRows, updateState } = useThirdPartyFormWizardStore()

  const [status, setStatus] = useState('loading') // 'loading' | 'ready' | 'not-configured' | 'error'
  const [rows, setRows] = useState([])
  const [showReadOnly, setShowReadOnly] = useState(true)
  const [showUnmapped, setShowUnmapped] = useState(true)

  useEffect(() => {
    if (testMode) return

    const loadReviewData = async () => {
      setStatus('loading')

      try {
        const { mappedFormId, rows: fetchedRows } = await fetchReviewDataRows({
          formId: selectedFormId,
          clientNo: selectedMatter?.clientNo,
          matterNo: selectedMatter?.matterNo
        })

        if (mappedFormId == null) {
          setStatus('not-configured')
          return
        }

        setRows(fetchedRows)
        setStatus('ready')
      } catch (error) {
        console.error('Error fetching third-party form review data:', error)
        setStatus('error')
      }
    }

    loadReviewData()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [testMode, selectedFormId])

  const handleToggleSend = (formFieldId) => {
    setRows((previousRows) =>
      previousRows.map((row) => (row.formFieldId === formFieldId ? { ...row, send: !row.send } : row))
    )
  }

  const handleClickContinue = () => {
    const reviewData = rows
      .filter((row) => row.isMapped)
      .map((row) => ({ mappedFormFieldId: row.mappedFormFieldId, value: row.resolvedValue, send: row.send }))

    updateState({ key: 'reviewData', value: reviewData })
    updateStepState({ status: 'completed' })
    handlePrevNext(1)
  }

  const filteredRows = rows.filter(
    (row) => (showReadOnly || !row.isReadOnly) && (showUnmapped || row.isMapped)
  )

  return (
    <>
      <WizardPanel data-testid="ThirdPartyFormReviewData">
        <WizardIconTitle title="Review data" icon={LargeNotesIcon} sx={{ width: '100%', mb: 2 }} />
        <MUITypography variant="body1" sx={{ mt: 0, mb: 4 }}>
          Review the Osprey data that will populate the form, and choose which fields to send to FormEvo.
        </MUITypography>

        {status === 'loading' ? <LoadingState sx={{ py: 6, width: '100%' }} /> : null}

        {status === 'not-configured' ? (
          <MUIAlert severity="info" sx={{ width: '100%' }}>
            This form has not been configured for field mapping yet.
          </MUIAlert>
        ) : null}

        {status === 'error' ? (
          <MUIAlert severity="error" sx={{ width: '100%' }}>
            Couldn&apos;t load the field mapping data. Please try again.
          </MUIAlert>
        ) : null}

        {status === 'ready' ? (
          <MUIStack sx={{ width: '100%' }} gap={3}>
            <MUIStack direction="row" gap={4}>
              <MUIFormControlLabel
                control={<MUICheckbox checked={showReadOnly} onChange={(e) => setShowReadOnly(e.target.checked)} />}
                label="Show read-only fields"
              />
              <MUIFormControlLabel
                control={<MUICheckbox checked={showUnmapped} onChange={(e) => setShowUnmapped(e.target.checked)} />}
                label="Show unmapped fields"
              />
            </MUIStack>

            <MUIStack
              gap={2}
              data-testid="ThirdPartyFormReviewDataRows"
              sx={{ width: '100%', maxHeight: '420px', overflowY: 'auto' }}
            >
              {filteredRows.length ? (
                filteredRows.map((row) => (
                  <ReviewDataRow key={row.formFieldId} row={row} onToggleSend={handleToggleSend} />
                ))
              ) : (
                <MUITypography variant="body2" color="text.secondary">
                  No fields match the current filters.
                </MUITypography>
              )}
            </MUIStack>
          </MUIStack>
        ) : null}
      </WizardPanel>

      <WizardFooter
        wideMode={wideMode}
        handleClickContinue={handleClickContinue}
        handleClickBack={() => handlePrevNext(-1)}
        continueButtonText="Confirm"
        continueButtonDisabled={status !== 'ready'}
      />
    </>
  )
}

ThirdPartyFormReviewData.propTypes = {
  updateStepState: PropTypes.func,
  wideMode: PropTypes.bool,
  handlePrevNext: PropTypes.func,
  testMode: PropTypes.bool
}
